import { useEffect, useRef, useState } from "react";
import {
  adminListJournal,
  adminCreateJournal,
  adminUpdateJournal,
  adminDeleteJournal,
  adminUploadFile,
} from "@/lib/api";

/**
 * Journal — simple blog-style editor. Only three inputs matter:
 *   image · title · body
 * Slug + excerpt are auto-derived on the backend; publish state is toggled
 * from the list view with a single click.
 */

const emptyForm = { title: "", body: "", image_url: "" };

const AdminJournal = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | slug
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListJournal();
      setEntries(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm(emptyForm);
    setMsg("");
  };
  const startEdit = (e) => {
    setEditing(e.slug);
    setForm({ title: e.title, body: e.body || "", image_url: e.image_url || "" });
    setMsg("");
  };
  const cancel = () => {
    setEditing(null);
    setForm(emptyForm);
    setMsg("");
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    setMsg("");
    try {
      const res = await adminUploadFile(file);
      setForm((f) => ({ ...f, image_url: res.url }));
    } catch (_e) {
      setMsg("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { setMsg("A title is required"); return; }
    setSaving(true);
    setMsg("");
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body,
        image_url: form.image_url.trim() || null,
        published: true, // sensible default; can be toggled from the list
      };
      if (editing === "new") {
        await adminCreateJournal(payload);
      } else {
        // Preserve current publish state on edit
        const current = entries.find((x) => x.slug === editing);
        await adminUpdateJournal(editing, { ...payload, published: current?.published !== false });
      }
      await load();
      setEditing(null);
      setForm(emptyForm);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (entry) => {
    try {
      await adminUpdateJournal(entry.slug, {
        title: entry.title,
        body: entry.body || "",
        image_url: entry.image_url || null,
        published: !entry.published,
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    }
  };

  const removeEntry = async (slug) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await adminDeleteJournal(slug);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const isForm = editing !== null;

  // ─────────────────────────── Editor form ───────────────────────────

  if (isForm) {
    return (
      <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="admin-journal-form">
        <button
          type="button"
          onClick={cancel}
          data-testid="admin-journal-back"
          className="font-badge text-[10px] tracking-[0.28em] uppercase text-[#8A7568] hover:text-[var(--gl-orange-hover)] mb-8"
        >
          ← Back to all posts
        </button>

        {/* Image drop zone */}
        <div className="mb-8">
          {form.image_url ? (
            <div className="relative group">
              <img src={form.image_url} alt="" className="w-full aspect-[3/2] object-cover rounded-2xl" />
              <button
                type="button"
                onClick={() => setForm({ ...form, image_url: "" })}
                data-testid="admin-journal-image-remove"
                className="absolute top-3 right-3 bg-white/95 border border-[#241C18] rounded-full px-3 py-1.5 text-[10px] font-badge tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 transition"
              >
                Change image
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="admin-journal-image-drop"
              className="w-full aspect-[3/2] rounded-2xl bg-[#FFF3E6] border-2 border-dashed border-[#F1DCC5] hover:border-[var(--gl-orange-hover)] hover:bg-[#FFE9D6] transition flex flex-col items-center justify-center gap-2 text-[#8A7568]"
            >
              <span className="font-serif-display text-3xl text-[#241C18]">
                {uploading ? "Uploading…" : "+ Add cover image"}
              </span>
              <span className="font-badge text-[10px] tracking-[0.28em] uppercase">
                Click or drop a photo here
              </span>
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => uploadImage(e.target.files?.[0])}
          />
        </div>

        {/* Title */}
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Give your post a headline…"
          data-testid="admin-journal-title"
          className="w-full text-3xl md:text-5xl font-serif-display leading-tight text-[#241C18] placeholder-[#C7B9A6] bg-transparent border-0 border-b border-[#F1DCC5] focus:border-[var(--gl-orange)] outline-none pb-3 mb-6"
        />

        {/* Body */}
        <textarea
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          placeholder={"Start writing…\n\nUse a blank line between paragraphs."}
          data-testid="admin-journal-body"
          rows={16}
          className="w-full text-base md:text-lg leading-relaxed text-[#241C18] placeholder-[#C7B9A6] bg-transparent border-0 outline-none resize-y font-sans-clean"
        />

        {msg && (
          <div className="mt-2 text-sm text-[#C97A63] font-typewriter" data-testid="admin-journal-msg">{msg}</div>
        )}

        {/* Sticky save bar */}
        <div className="sticky bottom-4 mt-10 flex flex-wrap items-center justify-between gap-3 bg-white/95 backdrop-blur border border-[#F1DCC5] rounded-full px-5 py-3 shadow-sm">
          <span className="text-xs font-typewriter text-[#8A7568]">
            {editing === "new" ? "New post" : `Editing · ${editing}`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={cancel}
              data-testid="admin-journal-cancel"
              className="px-4 py-2 text-xs font-badge tracking-[0.22em] uppercase text-[#241C18] hover:text-[var(--gl-orange-hover)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !form.title.trim()}
              data-testid="admin-journal-save"
              className="px-5 py-2 bg-[var(--gl-orange)] text-[var(--gl-orange-text)] rounded-full text-xs font-badge tracking-[0.22em] uppercase hover:bg-[var(--gl-orange-hover)] hover:text-white transition disabled:opacity-40"
            >
              {saving ? "Publishing…" : editing === "new" ? "Publish post" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────── List view ───────────────────────────

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-journal-tab">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#241C18]">Journal</h2>
          <p className="mt-2 text-sm text-[#8A7568] max-w-xl">
            Simple posts, blog-style. Add a cover image, headline, and body — that&rsquo;s it.
          </p>
        </div>
        <button
          type="button"
          onClick={startNew}
          data-testid="admin-journal-new-btn"
          className="shrink-0 px-5 py-2.5 bg-[var(--gl-orange)] text-[var(--gl-orange-text)] rounded-full text-xs font-badge tracking-[0.22em] uppercase hover:bg-[var(--gl-orange-hover)] hover:text-white transition"
        >
          + New post
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#8A7568]">Loading…</div>
      ) : entries.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#F1DCC5] rounded-2xl p-12 text-center"
          data-testid="admin-journal-empty"
        >
          <div className="font-serif-display text-3xl text-[#241C18] mb-2">No posts yet</div>
          <p className="text-sm text-[#8A7568] mb-6">Write your first story to bring the /journal page to life.</p>
          <button
            type="button"
            onClick={startNew}
            className="px-5 py-2.5 bg-[#241C18] text-[var(--gl-orange)] rounded-full text-xs font-badge tracking-[0.22em] uppercase"
            data-testid="admin-journal-empty-cta"
          >
            + New post
          </button>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="admin-journal-list">
          {entries.map((e) => (
            <article
              key={e.id}
              className="bg-white border border-[#F1DCC5] rounded-2xl p-4 md:p-5 flex gap-4 items-center"
              data-testid={`admin-journal-row-${e.slug}`}
            >
              <button
                type="button"
                onClick={() => startEdit(e)}
                className="w-20 h-20 md:w-24 md:h-24 shrink-0 rounded-xl overflow-hidden bg-[#FFE9D6] block"
                aria-label={`Edit ${e.title}`}
              >
                {e.image_url ? (
                  <img src={e.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-[#8A7568] font-badge tracking-wider">
                    NO IMAGE
                  </div>
                )}
              </button>

              <button
                type="button"
                onClick={() => startEdit(e)}
                className="flex-1 min-w-0 text-left"
                data-testid={`admin-journal-edit-${e.slug}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-serif-display text-lg md:text-xl text-[#241C18] leading-tight truncate">{e.title}</h3>
                  {!e.published && (
                    <span className="font-badge text-[9px] tracking-[0.22em] bg-[#8A7568] text-white px-2 py-0.5 rounded-full">DRAFT</span>
                  )}
                </div>
                {e.excerpt && <p className="text-xs md:text-sm text-[#6B6862] mt-1 line-clamp-2">{e.excerpt}</p>}
                <div className="text-[10px] font-typewriter text-[#8A7568] mt-1 truncate">/journal/{e.slug}</div>
              </button>

              <div className="flex flex-col gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => togglePublish(e)}
                  data-testid={`admin-journal-toggle-${e.slug}`}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-badge tracking-[0.22em] uppercase border transition ${e.published ? "border-[#241C18] text-[#241C18] hover:bg-[#241C18] hover:text-white" : "border-[var(--gl-orange-hover)] text-[var(--gl-orange-hover)] hover:bg-[var(--gl-orange)] hover:text-[var(--gl-orange-text)]"}`}
                >
                  {e.published ? "Unpublish" : "Publish"}
                </button>
                <button
                  type="button"
                  onClick={() => removeEntry(e.slug)}
                  data-testid={`admin-journal-delete-${e.slug}`}
                  className="px-3 py-1.5 rounded-full text-[10px] font-badge tracking-[0.22em] uppercase border border-[#C97A63] text-[#C97A63] hover:bg-[#C97A63] hover:text-white transition"
                >
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminJournal;
